import { useEffect } from 'react';
import { isPathbuddyHost } from '../../config/eduHost';
import { stripAppBasename } from '../../config/appBase';
import { resolvePageTitle, resolvePageDescription } from '../../config/pageTitles';
import { buildEduJsonLd, resolveEduCanonical } from '../../config/eduSeo';

/** The link preview image (1200 x 630). The same URL is in public/index.html, for previewers that do not run JS. */
export const OG_IMAGE_URL = 'https://ivoriai.com/og-image-v2.jpg';

/**
 * Reusable SEO Metadata Helper Component
 * Dynamically updates document title, meta tags, Open Graph, Twitter cards, and canonical URL.
 * Essential for Google Search Console, Google Ads Quality Score, and social sharing preview.
 */
export default function SeoMeta({
  title,
  description,
  keywords,
  url,
  type = 'website',
  schema
}) {
  useEffect(() => {
    const onPathbuddy = typeof window !== 'undefined' && isPathbuddyHost();
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    // Router-relative: the title/SEO resolvers key off "/lnat", not "/edu/lnat".
    const pathname = typeof window !== 'undefined' ? stripAppBasename(window.location.pathname) : '/';
    const resolvedTitle = title || resolvePageTitle(pathname, hostname);
    const resolvedDescription = description || resolvePageDescription(pathname, hostname);
    const resolvedKeywords = keywords || (onPathbuddy
      ? 'Pathbuddy, LNAT prep, TMUA prep, UCAT prep, TARA prep, ESAT prep, Oxbridge interview prep, exam prep AI'
      : 'ivori, ivori ai, reasoning ai, admissions test prep, LNAT prep, TMUA prep, UCAT prep, ESAT prep, decision intelligence, case study sandbox');
    const siteName = onPathbuddy ? 'Pathbuddy' : 'ivori';
    const currentUrl = onPathbuddy
      ? (url || window.location.href.split('#')[0])
      : resolveEduCanonical({
        pathname,
        hostname,
        requestedUrl: url,
        href: window.location.href,
      });

    // 1. Update Document Title
    document.title = resolvedTitle;

    // 2. Helper to set or create meta tags safely
    const setMetaTag = (selector, attrName, attrValue, contentValue) => {
      if (!contentValue) return;
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', contentValue);
    };

    // 3. Standard Meta Tags
    setMetaTag('meta[name="description"]', 'name', 'description', resolvedDescription);
    setMetaTag('meta[name="keywords"]', 'name', 'keywords', resolvedKeywords);

    // 4. Open Graph Meta Tags (Facebook, LinkedIn, iMessage, WhatsApp)
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', resolvedTitle);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', resolvedDescription);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', currentUrl);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', type);
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', siteName);
    setMetaTag('meta[property="og:locale"]', 'property', 'og:locale', 'en_GB');
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', OG_IMAGE_URL);

    // 5. Twitter Card Meta Tags
    setMetaTag('meta[property="twitter:card"]', 'property', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[property="twitter:site"]', 'property', 'twitter:site', '@ivoriai');
    setMetaTag('meta[property="twitter:title"]', 'property', 'twitter:title', resolvedTitle);
    setMetaTag('meta[property="twitter:description"]', 'property', 'twitter:description', resolvedDescription);
    setMetaTag('meta[property="twitter:url"]', 'property', 'twitter:url', currentUrl);
    setMetaTag('meta[property="twitter:image"]', 'property', 'twitter:image', OG_IMAGE_URL);

    // 6. Canonical Link Tag
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

    // 7. Dynamic Schema.org Structured Data for Google Search Rich Snippets
    let schemaScript = document.querySelector('#seo-meta-jsonld');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('id', 'seo-meta-jsonld');
      schemaScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(schemaScript);
    }

    const isLnat = currentUrl.toLowerCase().includes('lnat');
    const isTmua = currentUrl.toLowerCase().includes('tmua');

    const pathbuddySchema = {
      "@context": "https://schema.org",
      "@type": isLnat || isTmua ? "Course" : "WebSite",
      "name": resolvedTitle,
      "description": resolvedDescription,
      "url": currentUrl,
      "provider": {
        "@type": "Organization",
        "name": "Pathbuddy",
        "sameAs": "https://pathbuddy.com"
      },
      "offers": {
        "@type": "Offer",
        "price": "60.00",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock"
      }
    };

    const schemaData = schema || (onPathbuddy
      ? pathbuddySchema
      : buildEduJsonLd({
        title: resolvedTitle,
        description: resolvedDescription,
        url: currentUrl,
        pathname,
      }));

    schemaScript.textContent = JSON.stringify(schemaData);

  }, [title, description, keywords, url, type, schema]);

  return null;
}
